/**
 * Lobber — the RUN-AND-GUN arc-thrower (step 4, last-2). Indirect fire that punishes camping.
 *
 * A stationary ground thrower that, while the player is within horizontal range (regardless of
 * height or cover — the grenade ARCS over it), lobs a gravity-affected enemy PROJECTILE aimed
 * BALLISTICALLY to land at the player's current x. Stand still and the arc drops on you; move and
 * it lands where you were. Stompable + shootable infantry (one hit); contact hurts.
 *
 * Determinism: integer counters + a closed-form ballistic solve (vx = dx·g / 2·UP) in pure float
 * arithmetic; the grenade emits through ctx.spawnEntityAt's deterministic queue and arcs via the
 * Projectile `gravity` prop. No random / wall-clock / transcendentals.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { PROJECTILE_H } from './Projectile';

const GRAVITY = 2050;
const TERMINAL = 1200;
const SQUASH_SECONDS = 0.4;
const DEFAULT_COOLDOWN = 120;
const DEFAULT_TELEGRAPH = 22;
const DEFAULT_RANGE_PX = 900;
const GRENADE_GRAV = 1400;   // the grenade's own downward accel (px/s²) — a lazy arc
const GRENADE_UP = 720;      // launch vy (px/s, upward)
const GRENADE_MAX_VX = 620;  // clamp so a far player doesn't get a flat cannon shot
const GRENADE_LIFE = 200;

export class Lobber implements Entity {
  readonly type = EntityType.LOBBER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true;
  alive = true;
  squashTimer = 0;

  private cooldown: number;
  private telegraph: number;
  private rangePx: number;
  private sinceThrow = 0;
  private throwPose = false;

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 48, h = 52) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.cooldown = typeof props?.fireCooldownSteps === 'number'
      ? Math.max(20, Math.floor(props.fireCooldownSteps)) : DEFAULT_COOLDOWN;
    this.telegraph = typeof props?.telegraphSteps === 'number'
      ? Math.max(1, Math.floor(props.telegraphSteps)) : DEFAULT_TELEGRAPH;
    this.rangePx = typeof props?.rangePx === 'number' ? props.rangePx : DEFAULT_RANGE_PX;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;
    // gravity + ground (stands where placed)
    this.vy = Math.min(this.vy + GRAVITY * dt, TERMINAL);
    if (grid.collideY(this.body, this.vy * dt, false).grounded) this.vy = 0;

    const pcx = ctx.player.body.x + ctx.player.body.w / 2;
    const scx = this.body.x + this.body.w / 2;
    const dx = pcx - scx;
    this.facing = dx < 0 ? -1 : 1;

    const inRange = Math.abs(dx) <= this.rangePx;
    if (inRange) {
      this.sinceThrow++;
      this.throwPose = this.sinceThrow >= this.cooldown - this.telegraph;
      if (this.sinceThrow >= this.cooldown) { this.lob(ctx, dx); this.sinceThrow = 0; this.throwPose = false; }
    } else {
      this.sinceThrow = 0;
      this.throwPose = false;
    }
  }

  private lob(ctx: EntityContext, dx: number): void {
    // Ballistic solve: launch vy=-UP under accel GRAV → flight back to launch height takes
    // t = 2·UP/GRAV; to land at the player's x, vx = dx / t = dx·GRAV / (2·UP). Clamp the speed.
    const t = (2 * GRENADE_UP) / GRENADE_GRAV;
    const vx = Math.max(-GRENADE_MAX_VX, Math.min(GRENADE_MAX_VX, dx / t));
    const muzzleCx = this.body.x + this.body.w / 2 + this.facing * (this.body.w * 0.35);
    const muzzleCy = this.body.y + this.body.h * 0.2; // thrown from overhead
    ctx.spawnEntityAt(EntityType.PROJECTILE, muzzleCx, muzzleCy + PROJECTILE_H / 2, {
      vx, vy: -GRENADE_UP, gravity: GRENADE_GRAV, life: GRENADE_LIFE,
      owner: 'enemy', sprite: 'grenade', rw: 26, rh: 26,
    });
    ctx.playSound('shoot', 0.28, 0.5); // POLISH_01 #1 — enemy lob (generic low attack clip)
  }

  onPlayerOverlap(ctx: EntityContext): void {
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip
    } else {
      ctx.hurtPlayer();
    }
  }

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
  }

  damageable(): boolean { return true; }

  spriteKey(): string {
    if (!this.alive) return 'lobber_rest';
    return this.throwPose ? 'lobber_throw' : 'lobber_rest';
  }
  visible(): boolean { return this.alive || this.squashTimer > 0; }
}
